import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { NextToGoConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { EventLinkConfig, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackingSource } from '@frontend/sports/tracking/feature';

import { OptionGroupService } from '../../event-model/helpers/option-group.service';

@Component({
    selector: 'ms-next-to-go-event',
    templateUrl: './next-to-go-event.component.html',
    styleUrls: ['../next-to-go.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class NextToGoEventComponent implements OnInit, OnChanges {
    @Input() event: GridEvent;
    @Input() linkConfig: EventLinkConfig = {
        emptyText: this.sitecore.eventGrid.emptyMarketLinkText,
    };

    @Input() eventsCount: number;
    @Input() isWideScreen: boolean;
    @Input() marketgridGroupIds: NumberDictionary<string>;
    @Input() tracking: TrackingSource;
    @Input() oddsTimerGroupClass: string;

    eventUrl: string;
    shouldRemoveHref: boolean;
    emptyText: string;
    showOdds: boolean;
    empty: boolean;
    sportsToDisplayCompetitionLogos: number[];
    intervalForRedPrematchTimer: number;
    showCompetitionLogo: boolean;
    optionGroup: EventOptionGroup;
    showParticipantImage: boolean;
    hideCompetitionLogoOrSportIcon: boolean;

    @HostBinding()
    get class(): string {
        return this.getHostClass();
    }

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private nextToGoConfig: NextToGoConfig,
        private competitionLogoService: CompetitionLogosService,
        private optionGroupService: OptionGroupService,
    ) {
        this.showOdds = this.nextToGoConfig.showOdds;
        this.sportsToDisplayCompetitionLogos = this.nextToGoConfig.sportsToDisplayCompetitionLogo;
        this.intervalForRedPrematchTimer = this.nextToGoConfig.intervalForRedPreMatchTimer;
        this.showParticipantImage = this.nextToGoConfig.showParticipantLogoOrImage;
    }

    ngOnInit(): void {
        this.setLink();
        this.showCompetitionLogo = this.displayCompetitionLogo();
        const option = this.getOptionGroup(this.event);
        this.optionGroup = option ? option : <EventOptionGroup>{};
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
    }

    ngOnChanges(): void {
        const option = this.getOptionGroup(this.event);
        this.optionGroup = option ? option : <EventOptionGroup>{};
        this.hideCompetitionLogoOrSportIcon =
            this.showParticipantImage &&
            this.event.participants.some((x) => x.image) &&
            this.event.participants.length === 2 &&
            !this.event.isTournamentGame();
    }

    private setLink(): void {
        this.eventUrl = this.urlHelper.getEventUrl(this.event, this.linkConfig.extraParams);
        this.emptyText = this.linkConfig.emptyText;
    }

    private displayCompetitionLogo(): boolean {
        const isLogoVisible = this.sportsToDisplayCompetitionLogos.includes(this.event.sport.id);

        return isLogoVisible && this.competitionLogoService.hasLogo(this.event.sport.id, this.event.league.id);
    }

    private getHostClass(): string {
        if (this.isWideScreen && this.showOdds) {
            return 'next-to-go-event_odds ms-active-highlight';
        }
        if (this.isWideScreen && this.eventsCount === 1 && !this.showOdds) {
            return 'next-to-go-event_single ms-active-highlight';
        }

        return 'next-to-go-event ms-active-highlight';
    }

    private getOptionGroup(gridEvent: GridEvent): EventOptionGroup | undefined {
        const option = this.optionGroupService.get(gridEvent.optionGroups, this.marketgridGroupIds[this.event.sport.id]);
        if (!option) {
            this.empty = true;
        }

        return option;
    }
}
